import { useRef, useState } from 'react'

import { ActStrip, useConfirmAfterApplied } from './ActStrip.jsx'
import { REVIEW_MAX_CHARS, reviewCodePointLength, truncateToCodePoints } from '../lib/reviewText.js'
import { finishPanelDnfLine } from './reviewDisclaimer.js'
import { joinableId, subscriptionsOf } from './watch.js'
import {
  KIND_SET_RATING,
  KIND_SET_REVIEW,
  TARGET_STOPPED,
  UNREACHABLE,
  ratingCommand,
  reviewCommand,
  useWatchRequest,
} from './watchRequests.js'

// V2-4c: the FinishPanel — `docs/design-briefs/watch/v2/FinishPanel.dc.html`,
// verbatim copy (brief §2). A 1-10 rating and a 500-code-point review, both
// optional, sent as up to two independent `watch.set_rating`/
// `watch.set_review` requests -- "one request per kind that changed," never
// a combined write.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 TWO INDEPENDENT `useWatchRequest()` INSTANCES, NEVER ONE COMBINED ASK
// ═══════════════════════════════════════════════════════════════════════════
//
// `ActionBar.jsx`'s own header already states the rule this panel follows:
// "do not write a second request hook," and "a member can still press two
// controls as two genuinely separate in-flight requests." A rating-only
// edit must not wait on a review request nobody asked to send, and vice
// versa -- so `ratingRequest`/`reviewRequest` are two independent
// `useWatchRequest()` calls, each with its own `ActStrip`, exactly the
// shape `ActionBar` already composes for the status control and the
// stepper.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE COUNTER COUNTS CODE POINTS -- `site/src/lib/reviewText.js`, NEVER A
//    NAIVE `.length` / `.slice()`
// ═══════════════════════════════════════════════════════════════════════════
//
// The prototype's own `onReview` handler does `e.target.value.slice(0, 500)`
// -- UTF-16 code UNITS, which cuts an emoji's surrogate pair in half and
// under-counts a 500-emoji review by half. Copied here as PROSE (what the
// control does: caps the review at 500), never as code -- `truncateToCode
// Points`/`reviewCodePointLength` are the one shared definition the site's
// own Function (`_requestBody.js`) validates against, so what the counter
// shows can never disagree with what the server accepts.

/** `mine`'s own rating (a real 1-10 number) or `null` — never `undefined`,
 * so `useState`'s initial value is a stable, comparable type. */
function initialRatingOf(mine) {
  return typeof mine?.rating === 'number' && Number.isFinite(mine.rating) ? mine.rating : null
}

function initialReviewTextOf(mine) {
  return typeof mine?.review?.text === 'string' ? mine.review.text : ''
}

/**
 * `subscription` is the title being rated (the SAME object `ActionBar`
 * already holds — a listed row or a title-page subscription, both carry
 * `members[]`). `userId`/`onChanged` are `ActionBar`'s own, passed straight
 * through (the identical confirm-after-applied mechanism every other
 * control on this title already uses — `watchTitle.js::confirm`'s own
 * one-element `{subscriptions: [...]}` wrapper is what lets this component
 * read a fresh answer the same way whether it is mounted from a row or the
 * title page). `onClose` collapses the panel back to its "Rate it" /
 * "Review it" button — *Not now*, and (in this build's own judgement call,
 * see the report) after a successful save the panel stays open showing the
 * now-saved values, rather than auto-closing.
 */
export function FinishPanel({ subscription, userId, onChanged, onClose }) {
  const mine = subscription?.members?.find((member) => joinableId(member.id) === userId) ?? null
  const isMovie = subscription?.kind === 'movie'
  const title = typeof subscription?.title === 'string' ? subscription.title : ''
  const dnf = mine?.status === TARGET_STOPPED
  const dnfText = finishPanelDnfLine(mine?.status ?? null, mine?.progress ?? null, subscription?.episode_count ?? null)
  const heading = dnf ? `You stopped ${title} · review it anyway?` : `You ${isMovie ? 'watched' : 'finished'} ${title} · rate it`

  const initialRating = initialRatingOf(mine)
  const initialReviewText = initialReviewTextOf(mine)
  const [rating, setRating] = useState(initialRating)
  const [reviewText, setReviewText] = useState(initialReviewText)

  const ratingRequest = useWatchRequest()
  const reviewRequest = useWatchRequest()
  const askedRatingRef = useRef(null)
  const askedReviewRef = useRef('')

  const isRatingConfirmed = (shared) => {
    const fresh = subscriptionsOf(shared).find((row) => row.id === subscription?.id)
    const freshMine = fresh?.members?.find((member) => joinableId(member.id) === userId)
    return (freshMine?.rating ?? null) === askedRatingRef.current
  }
  const isReviewConfirmed = (shared) => {
    const fresh = subscriptionsOf(shared).find((row) => row.id === subscription?.id)
    const freshMine = fresh?.members?.find((member) => joinableId(member.id) === userId)
    const freshText = typeof freshMine?.review?.text === 'string' ? freshMine.review.text : null
    return freshText === askedReviewRef.current
  }
  const ratingConfirmFailed = useConfirmAfterApplied(ratingRequest, onChanged, isRatingConfirmed)
  const reviewConfirmFailed = useConfirmAfterApplied(reviewRequest, onChanged, isReviewConfirmed)

  const ratingChanged = rating !== initialRating
  const reviewChanged = reviewText !== initialReviewText
  const canSend = ratingChanged || reviewChanged

  const sendRating = () => {
    askedRatingRef.current = rating
    ratingRequest.ask({ kind: KIND_SET_RATING, target: subscription.id, payload: { rating } })
  }
  const sendReview = () => {
    const value = reviewText.trim() === '' ? null : reviewText
    askedReviewRef.current = value
    reviewRequest.ask({ kind: KIND_SET_REVIEW, target: subscription.id, payload: { text: value } })
  }
  const onSave = () => {
    if (ratingChanged) sendRating()
    if (reviewChanged) sendReview()
  }

  const anyUnreachable = ratingRequest.status === UNREACHABLE || reviewRequest.status === UNREACHABLE
  if (anyUnreachable) {
    const rc = ratingCommand(title, rating ?? askedRatingRef.current)
    const revc = reviewCommand(title)
    return (
      <div className="d2d-finishpanel d2d-finishpanel--unavail" data-testid="finish-panel" data-state="unavailable">
        <div className="d2d-micro d2d-finishpanel__kicker">Buttons are unavailable</div>
        <div className="d2d-finishpanel__unavailtext" data-testid="finish-panel-unavailable-text">
          /watch in Discord still works —{' '}
          <code className="d2d-actstrip__cmd">{revc}</code>
          {rc ? (
            <>
              {' and '}
              <code className="d2d-actstrip__cmd">{rc}</code>
            </>
          ) : null}
        </div>
      </div>
    )
  }

  if (ratingRequest.status || reviewRequest.status) {
    return (
      <div className="d2d-finishpanel" data-testid="finish-panel" data-state="pending">
        {ratingRequest.status ? (
          <ActStrip
            request={ratingRequest}
            confirmFailed={ratingConfirmFailed}
            testId="finish-panel-rating-status"
            confirmStaleTestId="finish-panel-rating-confirm-stale"
            doneText={askedRatingRef.current === null ? 'Your rating is cleared.' : `Rated ${askedRatingRef.current}/10.`}
            command={ratingCommand(title, askedRatingRef.current)}
            onRetry={sendRating}
          />
        ) : null}
        {reviewRequest.status ? (
          <ActStrip
            request={reviewRequest}
            confirmFailed={reviewConfirmFailed}
            testId="finish-panel-review-status"
            confirmStaleTestId="finish-panel-review-confirm-stale"
            doneText={askedReviewRef.current === null ? 'Your review is cleared.' : 'Your review is saved.'}
            command={reviewCommand(title)}
            onRetry={sendReview}
          />
        ) : null}
      </div>
    )
  }

  const count = reviewCodePointLength(reviewText)

  return (
    <div className="d2d-finishpanel" data-testid="finish-panel" data-state="editing">
      <div className="d2d-finishpanel__head">
        <span className="d2d-micro d2d-finishpanel__heading" data-testid="finish-panel-heading">
          {heading}
        </span>
        <span className="d2d-micro d2d-finishpanel__both">Both optional</span>
      </div>

      {dnf ? (
        <div role="note" className="d2d-finishpanel__dnf" data-testid="finish-panel-dnf">
          <div className="d2d-finishpanel__dnfhead">{'Your review will be marked "Didn\'t finish"'}</div>
          <div className="d2d-micro d2d-finishpanel__dnfline">{dnfText}</div>
        </div>
      ) : null}

      <div role="group" aria-label="Rating out of 10" className="d2d-finishpanel__ratings">
        {Array.from({ length: 10 }, (_, index) => index + 1).map((n) => (
          <button
            key={n}
            type="button"
            aria-pressed={rating === n}
            className={`d2d-finishpanel__ratebtn${rating === n ? ' d2d-finishpanel__ratebtn--active' : ''}`}
            data-testid={`finish-panel-rate-${n}`}
            onClick={() => setRating((current) => (current === n ? null : n))}
          >
            {n}
          </button>
        ))}
      </div>

      <label className="d2d-finishpanel__reviewlabel">
        <span className="d2d-micro d2d-finishpanel__reviewkicker">Short review</span>
        <textarea
          rows={4}
          value={reviewText}
          onChange={(event) => setReviewText(truncateToCodePoints(event.target.value, REVIEW_MAX_CHARS))}
          placeholder="What stayed with you? It stays blurred for anyone who hasn't finished it."
          className="d2d-finishpanel__reviewtext"
          data-testid="finish-panel-review-text"
        />
      </label>
      <div className="d2d-micro d2d-finishpanel__count" data-testid="finish-panel-count">
        {count} / {REVIEW_MAX_CHARS}
      </div>

      <div className="d2d-finishpanel__actions">
        <button
          type="button"
          className="d2d-finishpanel__save"
          data-testid="finish-panel-save"
          disabled={!canSend}
          onClick={onSave}
        >
          Send rating and review
        </button>
        <button
          type="button"
          className="d2d-finishpanel__cancel"
          data-testid="finish-panel-cancel"
          onClick={onClose}
        >
          Not now
        </button>
      </div>
    </div>
  )
}

export default FinishPanel
